import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import { generatePDF } from '../utils/pdfGenerator';
import { generateTravelPDF } from '../utils/travelPdfGenerator';
import { generateMarketingPDF } from '../utils/marketingPdfGenerator';
import { generateExcursionPDF } from '../utils/excursionPdfGenerator';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from './StripePaymentForm';

const { FiDownload, FiArrowLeft, FiCreditCard, FiLoader, FiX, FiAlertTriangle } = FiIcons;

const InvoicePreview = () => {
  const { id } = useParams();
  const { invoices, settings: localSettings, dataLoaded, updateInvoice } = useInvoice();
  
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(localSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isPaying, setIsPaying] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    if (!dataLoaded) {
      return; // Wait for local data to be loaded
    }

    const findInvoice = async () => {
      setIsLoading(true);
      setError(null);
      const localInvoice = invoices.find(inv => inv.id === parseInt(id));
      if (localInvoice) {
        setInvoice(localInvoice);
        setSettings(localSettings);
        setIsLoading(false);
      } else {
        try {
          const response = await fetch('/api/google-script-proxy', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'getInvoiceById', payload: { invoiceId: id } })
          });
          const result = await response.json();
          if (result.status === 'success') {
            setInvoice(result.data.invoice);
            setSettings(result.data.settings);
          } else {
            throw new Error(result.message || 'Invoice not found.');
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      }
    };
    findInvoice();
  }, [id, invoices, localSettings, dataLoaded]);

  const currencies = {
    USD: { symbol: '$', name: 'US Dollar' },
    EUR: { symbol: '€', name: 'Euro' },
    BRL: { symbol: 'R$', name: 'Brazilian Real' },
    GBP: { symbol: '£', name: 'British Pound' }
  };

  const getCurrencySymbol = (code) => {
    return currencies[code]?.symbol || '$';
  };

  if (isLoading || !dataLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FiLoader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Loading Invoice...</h3>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FiAlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-700 mb-2">Error</h3>
          <p className="text-gray-600">{error || 'The requested invoice could not be found.'}</p>
        </div>
      </div>
    );
  }

  const handleDownload = async () => {
    try {
      if (invoice.invoiceType === 'travel') {
        await generateTravelPDF(invoice);
      } else if (invoice.invoiceType === 'marketing') {
        await generateMarketingPDF(invoice);
      } else if (invoice.invoiceType === 'excursion') {
        await generateExcursionPDF(invoice);
      } else {
        await generatePDF(invoice);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handlePayment = async () => {
    if (!settings.stripePublishableKey) {
      alert('Stripe is not configured. Please add your Stripe Publishable Key in the settings.');
      return;
    }
    setIsPaying(true);
    setPaymentError(null);
    setStripePromise(loadStripe(settings.stripePublishableKey));

    try {
      if (invoice.isRecurring) {
        console.log("Initiating recurring payment...");
        const response = await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoice }),
        });
        const session = await response.json();
        if (!response.ok) throw new Error(session.error?.message || 'Failed to create checkout session.');
        
        if (session.url && session.sessionId) {
          console.log(`Checkout session created: ${session.sessionId}. Saving to invoice...`);
          updateInvoice(invoice.id, { checkoutSessionId: session.sessionId });
          console.log("Redirecting to Stripe...");
          window.location.href = session.url;
        } else {
          throw new Error('Failed to retrieve checkout URL or Session ID from server.');
        }

      } else {
        console.log("Initiating one-time payment...");
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoice }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to create payment intent.');
        
        const newClientSecret = data.clientSecret;
        const paymentIntentId = newClientSecret.split('_secret_')[0];
        
        console.log(`Payment intent created: ${paymentIntentId}. Saving to invoice...`);
        updateInvoice(invoice.id, { paymentIntentId: paymentIntentId });

        setClientSecret(newClientSecret);
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Payment Error:', error);
      setPaymentError(error.message);
    } finally {
      if (!invoice.isRecurring) {
        setIsPaying(false);
      }
    }
  };

  const formatPaymentDate = () => {
    const type = invoice.invoiceType || 'default';
    try {
      if (type === 'travel') {
        const dateToFormat = invoice.travelDate || invoice.createdAt;
        if (invoice.returnDate) {
          return `${format(new Date(dateToFormat), 'MMMM dd, yyyy')} - ${format(new Date(invoice.returnDate), 'MMMM dd, yyyy')}`;
        }
        return format(new Date(dateToFormat), 'MMMM dd, yyyy');
      }
      
      if (type === 'excursion') {
        const dateToFormat = invoice.excursionDetails?.excursionDate || invoice.createdAt;
        return format(new Date(dateToFormat), 'MMMM dd, yyyy');
      }
      
      const dateToFormat = invoice.paymentDate || invoice.createdAt;
      if (invoice.isDateRange && invoice.paymentEndDate) {
        return `${format(new Date(dateToFormat), 'MMMM dd, yyyy')} - ${format(new Date(invoice.paymentEndDate), 'MMMM dd, yyyy')}`;
      }
      return format(new Date(dateToFormat), 'MMMM dd, yyyy');
    } catch (e) {
      console.error("Error formatting date:", e);
      return "Invalid Date";
    }
  };

  const currencyCode = invoice.currency || invoice.pricing?.currency || 'USD';
  const currencySymbol = getCurrencySymbol(currencyCode);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {showPaymentModal && stripePromise && clientSecret && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <motion.div 
                className="bg-gray-100 rounded-xl shadow-2xl p-8 w-full max-w-md relative"
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <button onClick={() => setShowPaymentModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
                <div className="flex items-center mb-6">
                    <div className="p-3 bg-blue-100 rounded-full mr-4">
                        <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Complete Payment</h2>
                        <p className="text-gray-600">Enter your card details to pay securely.</p>
                    </div>
                </div>
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <StripePaymentForm 
                        total={invoice.total} 
                        currency={currencySymbol} 
                    />
                </Elements>
            </motion.div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <button
          onClick={() => window.history.back()}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
          Back
        </button>
        <div className="flex items-center gap-3">
          {settings.stripePublishableKey && (
            <motion.button
              onClick={handlePayment}
              disabled={isPaying}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isPaying ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2" />
                </motion.div>
              ) : (
                <SafeIcon icon={FiCreditCard} className="w-4 h-4 mr-2" />
              )}
              {isPaying ? 'Processing...' : 'Pay with Card'}
            </motion.button>
          )}
          <motion.button
            onClick={handleDownload}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
            Download PDF
          </motion.button>
        </div>
      </div>

      {paymentError && (
        <motion.div 
          className="my-4 p-4 bg-red-100 text-red-800 rounded-lg flex items-start"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-3 flex-shrink-0" />
            <div>
              <p className="font-bold">Payment Error</p>
              <pre className="text-sm whitespace-pre-wrap font-sans">{paymentError}</pre>
            </div>
        </motion.div>
      )}

      {/* Invoice Preview */}
      <motion.div
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div id="invoice-content" className="p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <img 
                src={invoice.companyInfo?.logoUrl || "https://01st.info/wp-content/uploads/2022/12/logo-v2.png"} 
                alt="Company Logo" 
                className="w-16 h-16 object-contain mb-4"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {invoice.companyInfo?.name || 'Your Company'}
                </h1>
                {invoice.companyInfo?.address && (
                  <p className="text-gray-600 whitespace-pre-line">{invoice.companyInfo.address}</p>
                )}
                {invoice.companyInfo?.phone && (
                  <p className="text-gray-600">Phone: {invoice.companyInfo.phone}</p>
                )}
                {invoice.companyInfo?.email && (
                  <p className="text-gray-600">Email: {invoice.companyInfo.email}</p>
                )}
                {invoice.companyInfo?.taxId && (
                  <p className="text-gray-600">Tax ID: {invoice.companyInfo.taxId}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
              <p className="text-lg font-semibold text-gray-700">#{invoice.number}</p>
              {(!('isExportation' in invoice) || !invoice.isExportation) && (
                <p className="text-gray-600">Date: {format(new Date(invoice.createdAt), 'MMMM dd, yyyy')}</p>
              )}
              <p className="text-lg font-semibold text-gray-700 mt-2">
                Currency: {currencyCode}
              </p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-900">{invoice.customer.name}</p>
              {invoice.customer.address && (
                <p className="text-gray-600 whitespace-pre-line">{invoice.customer.address}</p>
              )}
              {invoice.customer.phone && (
                <p className="text-gray-600">Phone: {invoice.customer.phone}</p>
              )}
              {invoice.customer.email && (
                <p className="text-gray-600">Email: {invoice.customer.email}</p>
              )}
            </div>
          </div>

          {/* Payment Info */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Date:</h4>
                <p className="text-gray-700">{formatPaymentDate()}</p>
              </div>
              {('isExportation' in invoice) && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Transaction Type:</h4>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    invoice.isExportation 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {invoice.isExportation ? 'Exportation' : 'Standard'}
                  </span>
                </div>
              )}
              {invoice.invoiceType === 'travel' && invoice.destination && (
                  <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Destination:</h4>
                      <p className="text-gray-700">{invoice.destination}</p>
                  </div>
              )}
              {invoice.invoiceType === 'excursion' && invoice.excursionDetails?.destination && (
                  <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Destination:</h4>
                      <p className="text-gray-700">{invoice.excursionDetails.destination}</p>
                  </div>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                    Item
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-900">
                    Qty
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-900">
                    Price
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-900">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {item.quantity}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right">
                      {currencySymbol}{item.price.toFixed(2)}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right font-medium">
                      {currencySymbol}{item.total.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{currencySymbol}{invoice.subtotal.toFixed(2)}</span>
              </div>
              {invoice.includeTax && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Tax ({invoice.taxRate}%):</span>
                  <span className="font-medium">{currencySymbol}{invoice.tax.toFixed(2)}</span>
                </div>
              )}
              {invoice.processingFee > 0 && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Processing Fee:</span>
                  <span className="font-medium">{currencySymbol}{invoice.processingFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t-2 border-gray-900">
                <span className="text-lg font-bold text-gray-900">Total:</span>
                <span className="text-lg font-bold text-gray-900">{currencySymbol}{invoice.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Notes:</h4>
              <p className="text-gray-700 whitespace-pre-line">{invoice.notes}</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default InvoicePreview;